import React from 'react';
import { AppProps, NextWebVitalsMetric } from 'next/app';
import { ThemeProvider } from 'react-jss';
import { WrenchTheme as theme, GlobalStyles } from '@wds/styles';
import { trace } from '@opentelemetry/api';
import { ApolloProvider } from 'react-apollo';
import { StoreObject } from 'apollo-cache-inmemory';
import { useApollo } from '~helpers/apolloClient';
import PageLayout from '~components/pageLayout';
import { LayoutType } from '~components/pageLayout/types';
import { StaticData } from '~helpers/pages/types';

type PageProps = {
  [dataId: string]: StoreObject | undefined;
  layout: LayoutType;
  staticData: StaticData;
};

const RespWeb = ({ Component, pageProps }: AppProps<PageProps>): JSX.Element => {
  const { layout, staticData, ...otherPageProps } = pageProps;
  const apolloClient = useApollo(otherPageProps);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />
      <ApolloProvider client={apolloClient}>
        <PageLayout layout={layout} staticData={staticData}>
          <Component {...otherPageProps} />
        </PageLayout>
      </ApolloProvider>
    </ThemeProvider>
  );
};

export default RespWeb;

/**
 * Get and send web vital metrics to SignalFX RUM
 * ref: https://nextjs.org/docs/advanced-features/measuring-performance
 *
 * @param metrics - web vital metrics provided by Next.js
 *
 */
export const reportWebVitals = (metrics: NextWebVitalsMetric): void => {
  const { name, value: durationInMs, startTime: relativeStartTime } = metrics;

  const endTime = Date.now();
  const startTime = endTime - durationInMs;

  const span = trace.getTracer('appModuleLoader').startSpan('Web Vital', { startTime });
  span.setAttributes({
    'workflow.name': name,
    'webVital.name': name,
    'webVital.durationInMs': durationInMs,
    'webVital.relativeStartTime': relativeStartTime,
  });
  span.end(endTime);
};
